/**
 * Henley Spirit bespoke collection page.
 * - <henley-filter-form>: wraps the filter/sort bar + slide-out filter
 *   drawer. Submits via the native collection filter/sort query params
 *   (value.param_name, sort_by) so it degrades to a normal page load
 *   without JS, and is AJAX-enhanced here via the Section Rendering API
 *   (?section_id=...), mirroring the pattern already used by the theme's
 *   own assets/facets.js, but scoped to this bespoke markup.
 * - Every page of results loads automatically into the existing grid, so the
 *   whole collection shows without a tap. The "Load All Products" button only
 *   stays usable if a page fails to load (tap to retry).
 * - Quick-add uses the shared window.HenleyCart helper from henley-product.js.
 */

(function () {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  if (!customElements.get('henley-filter-form')) {
    customElements.define('henley-filter-form', class HenleyFilterForm extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.wrapper = document.getElementById('HenleyCollectionMain-' + this.sectionId);
        this.debouncedRefresh = debounce(() => this.refresh(), 400);
        this.bind();

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.wrapper.classList.remove('henley-filter-drawer-open');
        });
      }

      bind() {
        const form = this.querySelector('form');
        if (!form) return;

        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.refresh();
        });

        form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
          el.addEventListener('change', () => this.refresh());
        });

        // Two sort <select> elements exist (the desktop top-bar one, which
        // is the only one with name="sort_by" and so the only one actually
        // submitted, and an unnamed mobile-drawer proxy) so changing either
        // one keeps both in sync before refreshing.
        const sortSelects = form.querySelectorAll('select[data-henley-sort]');
        sortSelects.forEach((el) => {
          el.addEventListener('change', () => {
            sortSelects.forEach((other) => { other.value = el.value; });
            this.refresh();
          });
        });

        form.querySelectorAll('input[type="number"]').forEach((el) => {
          el.addEventListener('input', this.debouncedRefresh);
        });

        this.querySelectorAll('[data-henley-open-filters]').forEach((btn) => {
          btn.addEventListener('click', () => this.wrapper.classList.add('henley-filter-drawer-open'));
        });
        this.querySelectorAll('[data-henley-close-filters]').forEach((btn) => {
          btn.addEventListener('click', () => this.wrapper.classList.remove('henley-filter-drawer-open'));
        });

        this.querySelectorAll('[data-henley-active-chips] a, [data-henley-clear-filters]').forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.refresh(link.getAttribute('href'));
          });
        });
      }

      refresh(explicitUrl) {
        const form = this.querySelector('form');
        let url = explicitUrl;
        if (!url) {
          const params = new URLSearchParams(new FormData(form));
          const query = params.toString();
          url = window.location.pathname + (query ? '?' + query : '');
        }

        const sep = url.includes('?') ? '&' : '?';
        this.wrapper.classList.add('henley-loading');

        fetch(`${url}${sep}section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');

            const newFormWrap = doc.getElementById('HenleyFilterFormWrap-' + this.sectionId);
            const currentFormWrap = document.getElementById('HenleyFilterFormWrap-' + this.sectionId);
            if (newFormWrap && currentFormWrap) currentFormWrap.innerHTML = newFormWrap.innerHTML;

            const newGrid = doc.getElementById('HenleyProductGridContainer-' + this.sectionId);
            const currentGrid = document.getElementById('HenleyProductGridContainer-' + this.sectionId);
            if (newGrid && currentGrid) currentGrid.innerHTML = newGrid.innerHTML;

            window.history.pushState({}, '', url);
            this.bind();
            document.dispatchEvent(new CustomEvent('henley:grid-updated'));
          })
          .catch((error) => console.error(error))
          .finally(() => {
            this.wrapper.classList.remove('henley-loading');
          });
      }
    });
  }

  // Load the next page of products into the grid. Resolves true when a page was added.
  function loadMore(btn) {
    const nextUrl = btn.dataset.nextUrl;
    const sectionId = btn.dataset.sectionId;
    if (!nextUrl || !sectionId || btn.dataset.loading) return Promise.resolve(false);

    const sep = nextUrl.includes('?') ? '&' : '?';
    btn.dataset.loading = 'true';
    btn.disabled = true;
    btn.textContent = 'Loading more products...';

    return fetch(`${nextUrl}${sep}section_id=${sectionId}`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        // A filter or sort replaced the grid while this page was loading: drop it
        if (!btn.isConnected) return false;
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const currentGrid = document.getElementById('HenleyProductGrid-' + sectionId);
        const newGrid = doc.getElementById('HenleyProductGrid-' + sectionId);
        if (currentGrid && newGrid) {
          Array.from(newGrid.children).forEach((card) => currentGrid.appendChild(card));
        }

        const currentWrap = btn.closest('[data-henley-load-more-wrap]');
        const newWrap = doc.querySelector('[data-henley-load-more-wrap]');
        if (currentWrap && newWrap) {
          // The next page counts only its own products ("Showing 5 of 53"): count everything now in the grid
          const count = newWrap.querySelector('[data-henley-showing-count]');
          if (count && currentGrid) count.textContent = count.textContent.replace(/\d+/, currentGrid.children.length);
          currentWrap.replaceWith(newWrap);
        } else if (currentWrap) {
          currentWrap.remove();
        }
        return true;
      })
      .catch((error) => {
        console.error(error);
        delete btn.dataset.loading;
        btn.disabled = false;
        btn.textContent = 'Load All Products';
        return false;
      });
  }

  // Customers shouldn't have to press a button: keep loading pages until the whole collection is shown.
  // If a page fails, the button comes back so it can be tapped to try again.
  function loadAllProducts() {
    const btn = document.querySelector('[data-henley-load-more]');
    if (!btn) return;
    loadMore(btn).then((added) => {
      if (added) loadAllProducts();
    });
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-henley-load-more]');
    if (!btn) return;
    event.preventDefault();
    loadMore(btn).then((added) => {
      if (added) loadAllProducts();
    });
  });

  // Start again whenever a filter or sort swaps the grid
  document.addEventListener('henley:grid-updated', loadAllProducts);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadAllProducts);
  } else {
    loadAllProducts();
  }
})();
